import { Product } from '@setvi/shared/interfaces';
import { MONGO_REALM_COLLECTIONS } from '@setvi/shared/services';
import { CompanySettingsTypeID } from '@setvi/shared/services/react-query/query/user/types';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRealmContext } from 'Providers/MongoRealmProvider/context';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

interface Props {
  product: Product;
}

export const useAssociatedProducts = ({ product }: Props) => {
  const [associatedProducts, setAssociatedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { client } = useRealmContext();
  const { companyData } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();

  const fetchAssociatedProducts = useCallback(async () => {
    try {
      const response = await client
        .db(
          companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
            ?.Value
        )
        .collection(MONGO_REALM_COLLECTIONS.PRODUCTS)
        .find({ sku: { $in: product.productAssociations || [] } });

      const transformedProducts = response.map((p: Product) => ({
        ...p,
        name: toTitleCase(p.name),
        ...(p.vendor && {
          vendor: {
            ...p.vendor,
            name: toTitleCase(p.vendor.name)
          }
        })
      }));

      setAssociatedProducts(transformedProducts);
    } catch (error) {
      enqueueSnackbar('Error occured while fetching Options and Accessories.', {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    client,
    companyData.Settings,
    enqueueSnackbar,
    product.productAssociations
  ]);

  useEffect(() => {
    fetchAssociatedProducts();
  }, [fetchAssociatedProducts]);

  return {
    associatedProducts,
    isLoading
  };
};
